/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { DragDropModule } from '@angular/cdk/drag-drop';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { CommonSharedModule } from '@gh/common-shared';
import {
  CoreUiCommonModule,
  CoreUiDialogsModule,
  CoreUiFormModule,
  CoreUiMenuModule,
  CoreUiTableLayoutModule,
  CoreUiUploaderModule,
  CoreUiWaitingModule,
  NotificationService,
  S3UploadAuthService,
  ToastService,
} from '@gh/core-ui';
import { CoreUIGridModule, GridLayoutService } from '@gh/core-ui-grid';
import {
  CheckboxTreeComponent,
  CheckboxTreeItemComponent,
  ConfirmationDialogComponent,
  DesktopPageEnvironmentDirective,
  desktopPageEnvironmentStyleFactory,
  DialogButtonBarComponent,
  DialogCloseDirective,
  DialogComponent,
  DialogContentComponent,
  DialogHeaderComponent,
  DownloadDialogComponent,
  EntityInputDialogComponent,
  FileInputDirective,
  FileListComponent,
  FormMenuOptionDirective,
  FormToolbarOptionsCancelDirective,
  FormToolbarOptionsComponent,
  GoogleFilePickerComponent,
  GridSettingsDialogComponent,
  ImageViewerDialogComponent,
  InputFileComponent,
  LiftComponent,
  LiftContainer,
  ListComponent,
  ListHeaderComponent,
  ListItemComponent,
  ListItemContainerComponent,
  ListItemPointerComponent,
  LogDialogComponent,
  LogDialogPreloadService,
  MessageDialogComponent,
  NavigationComponent,
  NavigationItemComponent,
  NavigationService,
  NavLinkContentDirective,
  NavTabBarComponent,
  NavTabComponent,
  NoEntryComponent,
  NotFoundPageComponent,
  NotImplementedPageComponent,
  PAGE_ENVIRONMENT_STYLE_FACTORIES,
  PageComponent,
  PageContentComponent,
  PageEnvironmentManager,
  PageErrorComponent,
  PageHeaderComponent,
  PageMobileHeaderComponent,
  PanelComponent,
  PanelTriggerForDirective,
  PreloaderComponent,
  ResponsivePageEnvironmentDirective,
  RootLayoutAppBarPartDirective,
  RootLayoutComponent,
  RootLayoutContentPartDirective,
  RootToolbarComponent,
  SystemInfoDialogComponent,
  TabContentDirective,
  TabDirective,
  TabGroupComponent,
  TabOptionsDirective,
  TextListFilterComponent,
  TextMessageDialogComponent,
  ThumbnailComponent,
  TimerComponent,
  ToolbarOptionsComponent,
  VisibilityFilterComponent,
  WizardButtonBarComponent,
  WizardComponent,
  WizardStepBeforeNextDirective,
  WizardStepDirective,
} from './components';
import { DelegatedRootLayout, RootLayout } from './components/root-layout/root-layout';
import { IfBreakpointDirective } from './directives';
import { DefaultGridLayoutService } from './grid';
import { MaterialInternalModule } from './material-internal.module';
import {
  DefaultNotificationService,
  NotificationContext,
  NotificationItemComponent,
  NotificationPanelComponent,
  NotificationQueueComponent,
} from './notifications';
import {
  DefaultToastService,
  ToastCardComponent,
  ToastContext,
  ToastPanelComponent,
} from './toasts';
import {
  ContextRouter,
  LeaveContextGuard,
} from './routing';
import {
  ContextableDialogComponent,
  ContextablePageComponent,
  ContextRouterComponent,
} from './routing/context';
import {
  AppS3UploadAuthService,
  BreakpointManager,
  BREAKPOINTS,
  DialogComponentService,
  GridSettingsService,
} from './services';

export const DEFAULT_BREAKPOINTS = {
  'xs': '(min-width: 0px) and (max-width: 599px)',
  'gt-xs': '(min-width: 600px)',
  'lt-sm': '(max-width: 599px)',
  'sm': '(min-width: 600px) and (max-width: 959px)',
  'gt-sm': '(min-width: 960px)',
  'lt-md': '(max-width: 959px)',
  'md': '(min-width: 960px) and (max-width: 1279px)',
  'gt-md': '(min-width: 1280px)',
  'lt-lg': '(max-width: 1279px)',
  'lg': '(min-width: 1280px) and (max-width: 1599px)',
  'gt-lg': '(min-width: 1600px)',
  'lt-xl': '(max-width: 1599px)',
  'xl': '(min-width: 1600px) and (max-width: 5000px)',

  // derived breakpoints
  'dialog-sm': '(max-width: 599px), (max-width: 959px) and (max-height: 479px)',
  'dialog-gt-sm': '(min-width: 960px), (min-height: 480px) and (min-width: 600px)',

  // breakpoints convenient for mobile screens
  'mobile': '(min-width: 0px) and (max-width: 767px)',
  'gt-mobile': '(min-width: 768px)',
};

@NgModule({
  imports: [
    DragDropModule,
    FormsModule,
    ReactiveFormsModule,

    CommonSharedModule.forChild(),
    MaterialInternalModule,

    CoreUiWaitingModule.forChild(),
    CoreUiFormModule.forChild(),
    CoreUiCommonModule.forChild(),
    CoreUiDialogsModule.forChild(),
    CoreUiUploaderModule.forChild(),
    CoreUiTableLayoutModule.forChild(),
    CoreUiMenuModule.forChild(),
    CoreUIGridModule.forChild(),
  ],
  declarations: [
    RootLayoutComponent,
    RootLayoutAppBarPartDirective,
    RootLayoutContentPartDirective,
    RootToolbarComponent,
    ToolbarOptionsComponent,
    FormMenuOptionDirective,
    FormToolbarOptionsCancelDirective,

    NavigationComponent,
    NavigationItemComponent,

    PageComponent,
    PageContentComponent,
    PageHeaderComponent,
    PageMobileHeaderComponent,
    PageErrorComponent,
    NotFoundPageComponent,
    NotImplementedPageComponent,

    CheckboxTreeComponent,
    CheckboxTreeItemComponent,

    ListComponent,
    ListItemComponent,
    ListItemPointerComponent,
    ListHeaderComponent,
    ListItemContainerComponent,

    NavTabBarComponent,
    NavTabComponent,
    NavLinkContentDirective,

    EntityInputDialogComponent,
    SystemInfoDialogComponent,
    FormToolbarOptionsComponent,
    TimerComponent,
    GoogleFilePickerComponent,
    FileListComponent,
    FileInputDirective,
    WizardButtonBarComponent,
    LogDialogComponent,
    VisibilityFilterComponent,
    DownloadDialogComponent,
    TextListFilterComponent,
    NoEntryComponent,
    PreloaderComponent,
    InputFileComponent,

    LiftComponent,
    WizardComponent,
    WizardStepDirective,
    WizardStepBeforeNextDirective,

    ThumbnailComponent,

    TabContentDirective,
    TabDirective,
    TabGroupComponent,
    TabOptionsDirective,

    PanelComponent,
    PanelTriggerForDirective,

    ContextRouterComponent,
    ContextablePageComponent,
    ContextableDialogComponent,

    TextMessageDialogComponent,
    ImageViewerDialogComponent,
    ConfirmationDialogComponent,
    MessageDialogComponent,

    DialogButtonBarComponent,
    DialogContentComponent,
    DialogHeaderComponent,
    DialogComponent,
    DialogCloseDirective,

    GridSettingsDialogComponent,

    NotificationPanelComponent,
    NotificationQueueComponent,
    NotificationItemComponent,
    ToastPanelComponent,
    ToastCardComponent,

    DesktopPageEnvironmentDirective,
    ResponsivePageEnvironmentDirective,
    IfBreakpointDirective,
  ],
            exports: [
              RootLayoutComponent,
              RootLayoutAppBarPartDirective,
              RootLayoutContentPartDirective,
              RootToolbarComponent,
              ToolbarOptionsComponent,
              FormMenuOptionDirective,
              FormToolbarOptionsCancelDirective,

              NavigationComponent,
              NavigationItemComponent,

              PageComponent,
              PageContentComponent,
              PageHeaderComponent,
              PageMobileHeaderComponent,

              CheckboxTreeComponent,

              ListComponent,
              ListItemComponent,
              ListItemPointerComponent,
              ListHeaderComponent,
              ListItemContainerComponent,

              NavTabBarComponent,
              NavTabComponent,
              NavLinkContentDirective,

              EntityInputDialogComponent,
              SystemInfoDialogComponent,

              FormToolbarOptionsComponent,
              TimerComponent,
              GoogleFilePickerComponent,
              FileListComponent,
              FileInputDirective,
              WizardButtonBarComponent,
              LogDialogComponent,
              VisibilityFilterComponent,
              TextListFilterComponent,
              NoEntryComponent,
              PreloaderComponent,
              InputFileComponent,

              LiftComponent,
              WizardComponent,
              WizardStepDirective,
              WizardStepBeforeNextDirective,

              ThumbnailComponent,

              TabContentDirective,
              TabDirective,
              TabGroupComponent,
              TabOptionsDirective,

              PanelComponent,
              PanelTriggerForDirective,

              ContextRouterComponent,

              DialogButtonBarComponent,
              DialogContentComponent,
              DialogHeaderComponent,
              DialogComponent,
              DialogCloseDirective,

              DesktopPageEnvironmentDirective,
              ResponsivePageEnvironmentDirective,
              IfBreakpointDirective,

              FormsModule,
              ReactiveFormsModule,

              CommonSharedModule,
              MaterialInternalModule,

              CoreUiWaitingModule,
              CoreUiFormModule,
              CoreUiCommonModule,
              CoreUiDialogsModule,
              CoreUiUploaderModule,
              CoreUiTableLayoutModule,
              CoreUiMenuModule,
              CoreUIGridModule,
              ConfirmationDialogComponent,
            ],
})
export class CommonInternalSharedModule {
  static forRoot(): ModuleWithProviders<CommonInternalSharedModule> {
    return {
      ngModule: CommonInternalSharedModule,
      providers: [
        {
          provide: MAT_DIALOG_DEFAULT_OPTIONS,
          useValue: { maxWidth: '90vw', maxHeight: '90vh', hasBackdrop: true, autoFocus: true },
        },
        { provide: MAT_SELECT_CONFIG, useValue: { panelWidth: null } },
        NavigationService,
        { provide: GridLayoutService, useClass: DefaultGridLayoutService },
        AppS3UploadAuthService,
        GridSettingsService,
        DialogComponentService,
        { provide: S3UploadAuthService, useClass: AppS3UploadAuthService },
        LogDialogPreloadService,
        LiftContainer,
        LeaveContextGuard,
        ContextRouter,
        DelegatedRootLayout,
        { provide: RootLayout, useExisting: DelegatedRootLayout },
        NotificationContext,
        { provide: NotificationService, useClass: DefaultNotificationService },
        ToastContext,
        { provide: ToastService, useClass: DefaultToastService },
        PageEnvironmentManager,
        {
          provide: PAGE_ENVIRONMENT_STYLE_FACTORIES,
          useValue: {
            'desktop': desktopPageEnvironmentStyleFactory,
          },
        },
        BreakpointManager,
        { provide: BREAKPOINTS, useValue: DEFAULT_BREAKPOINTS },
      ],
    };
  }

  static forChild(): ModuleWithProviders<CommonInternalSharedModule> {
    return {
      ngModule: CommonInternalSharedModule,
      providers: [],
    };
  }
}
